import { useRef, useState } from "react";
import { Upload, Download, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { download, errMsg } from "../../lib/api";
import { Card, StatusBadge } from "../common";
import { Button } from "../ui/button";
import { ProspectFields, EMPTY_PROSPECT, useRunWatcher } from "./shared";

const OUTCOME = { new: "New prospect saved", duplicate: "Duplicate blocked", excluded: "Excluded", out_of_region: "Outside target regions" };

function ImportCard({ onImported }) {
  const ref = useRef();
  const [rows, setRows] = useState(null);
  const { running, watch } = useRunWatcher((run) => {
    setRows(run.row_results || []);
    const s = run.stats || {};
    toast.success(`Import ${run.status}: ${s.new ?? 0} new, ${s.duplicates_blocked ?? 0} duplicates blocked, ${s.out_of_region ?? 0} out of region, ${s.excluded_irrelevant ?? 0} excluded`);
    onImported();
  });
  const upload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { const r = await api.post("/leadgen/import", fd); toast.info(`Processing ${r.data.rows} rows…`); setRows(null); watch(r.data.id); }
    catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <Card title="Import CSV / Excel list" testid="leadgen-import-card">
      <p className="text-sm text-muted-foreground mb-3">Upload lists you are permitted to use (licensed directory or B2B database exports, your own research). Every row is normalised, checked against all CRM leads, clients and earlier prospects, then scored. Rows outside your target locations are skipped.</p>
      <input type="file" accept=".csv,.xlsx" hidden ref={ref} onChange={upload} data-testid="leadgen-import-input" />
      <div className="flex flex-wrap gap-2">
        <Button className="vmw-gradient" onClick={() => ref.current.click()} disabled={running} data-testid="leadgen-import-button">{running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{running ? "Processing…" : "Upload file"}</Button>
        <Button variant="outline" onClick={() => download("/leadgen/import/template", "lead-agent-import-template.csv")} data-testid="leadgen-import-template"><Download className="h-4 w-4 mr-1" />Template</Button>
      </div>
      {rows && (
        <div className="mt-4 max-h-72 overflow-y-auto border rounded-lg" data-testid="leadgen-import-results">
          <table className="vmw-table"><thead><tr><th>Row</th><th>Business</th><th>Result</th><th>Detail</th></tr></thead>
            <tbody>{rows.map((r) => <tr key={r.row}><td>{r.row}</td><td>{r.business_name}</td><td><StatusBadge value={OUTCOME[r.outcome] || r.outcome} /></td><td className="text-xs">{r.detail}</td></tr>)}</tbody></table>
        </div>
      )}
    </Card>
  );
}

function ManualCard({ onSaved }) {
  const [f, setF] = useState(EMPTY_PROSPECT);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/leadgen/prospects/manual", f);
      setRes(r.data);
      if (r.data.outcome === "new") { toast.success(`Saved ${r.data.prospect.code} (score ${r.data.prospect.score})`); setF(EMPTY_PROSPECT); onSaved(); }
      else toast.warning(r.data.outcome === "duplicate" ? `Duplicate of ${r.data.match.type} ${r.data.match.code} — not added` : r.data.reason);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Manual research entry" testid="leadgen-manual-card">
      <p className="text-sm text-muted-foreground mb-3">For businesses you researched yourself (search engines, directories, public social profiles). Only enter information that is publicly listed; leave unknown fields blank.</p>
      <ProspectFields f={f} setF={setF} prefix="manual" />
      <div className="flex items-center gap-3 mt-3">
        <Button className="vmw-gradient" disabled={busy || f.business_name.trim().length < 2} onClick={save} data-testid="manual-save"><Plus className="h-4 w-4 mr-1" />{busy ? "Checking…" : "Check & save prospect"}</Button>
        {res && <span className="text-sm" data-testid="manual-result">{OUTCOME[res.outcome]}{res.match ? `: ${res.match.type} ${res.match.code} (${res.match.fields.join(", ")})` : ""}{res.flagged_matches?.length ? " — flagged for duplicate review" : ""}</span>}
      </div>
    </Card>
  );
}

export default function AddProspects({ onChanged }) {
  return (
    <div className="grid xl:grid-cols-2 gap-4" data-testid="leadgen-add">
      <ImportCard onImported={onChanged} />
      <ManualCard onSaved={onChanged} />
    </div>
  );
}
